import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import useAuth from "hooks/useAuth";
import { useBusiness } from "@hooks/useBusiness";
import { useDashboardAnalytics } from "@hooks/useDashboardAnalytics";
import { fetchShifts } from "@/services/peopleApi";
import { formatCurrency } from "@/utils/formatCurrency";

// sparklines
import { Sparklines, SparklinesLine } from "react-sparklines";

import { useStorePath } from "@hooks/useStorePath";

// icons
import {
  IoEllipsisHorizontal,
  IoEyeOutline,
  IoChevronDown,
  IoChevronUp,
  IoGlobeOutline,
  IoPhonePortraitOutline,
  IoAddOutline,
  IoClose,
  IoStorefrontOutline,
} from "react-icons/io5";

// ---- Small building blocks -------------------------------------------------

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-[#e6e7ea] bg-white ${className}`}>{children}</div>
);

const CollapseChevron = ({ open, onClick }) => (
  <button
    onClick={onClick}
    className="flex h-7 w-7 items-center justify-center rounded-full border border-[#e0e1e4] text-[#555] hover:bg-[#f4f4f6] transition-colors"
    aria-label={open ? "Collapse" : "Expand"}
  >
    {open ? <IoChevronUp className="h-4 w-4" /> : <IoChevronDown className="h-4 w-4" />}
  </button>
);

const StatTile = ({ label, value, data, positive = true }) => (
  <div className="flex-1 min-w-[150px] rounded-xl bg-[#f6f7f9] px-4 py-3">
    <p className="text-[12px] text-[#5a6272]">{label}</p>
    <div className="mt-1 flex items-end justify-between gap-2">
      <span className="text-[20px] font-semibold text-[#1a1a1a]">{value}</span>
      <div className="h-6 w-[70px]">
        <Sparklines data={data} width={70} height={24} margin={2}>
          <SparklinesLine
            color={positive ? "#2b6ef2" : "#e0655b"}
            style={{ fill: "none", strokeWidth: 2 }}
          />
        </Sparklines>
      </div>
    </div>
  </div>
);

// ---- Page ------------------------------------------------------------------

const MainDashboard = () => {
  const { isAuthenticated, user } = useAuth();
  const { businessId, users } = useBusiness();
  const { storePath } = useStorePath();
  const { data: analytics } = useDashboardAnalytics();

  const { data: shifts = [] } = useQuery({
    queryKey: ["shifts", businessId, Object.keys(users).length],
    queryFn: () => fetchShifts(businessId, users),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  // The tiles show a headline figure; the sparkline is illustrative only, so it
  // reuses the daily revenue series rather than inventing per-tile history.
  const sparkline = (analytics?.salesPerformance || []).slice(-8).map((d) => d.value);

  const [storeOpen, setStoreOpen] = useState(true);
  const [resourcesOpen, setResourcesOpen] = useState(true);
  const [showAppPromo, setShowAppPromo] = useState(true);

  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // The staff record carries the full name; fall back to the email local part
  // while that record is still loading.
  const displayName = users[user?.uid]?.name || user?.displayName || user?.email || "";
  const firstName = displayName.split(/[@\s]/)[0];

  return (
    <div className="flex flex-col gap-5 pb-12">
      {/* Hero */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[26px] font-bold text-[#111]">
          Welcome back{firstName ? `, ${firstName}` : ""}
        </h1>
        <div className="flex items-center gap-2">
          <button
            aria-label="More"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#dcdde0] text-[#555] hover:bg-[#f4f4f6] transition-colors"
          >
            <IoEllipsisHorizontal className="h-5 w-5" />
          </button>
          <button className="flex items-center gap-2 rounded-full border border-[#dcdde0] bg-white px-4 py-2 text-[13.5px] font-semibold text-[#111] hover:bg-[#f4f4f6] transition-colors">
            <IoEyeOutline className="h-4 w-4" />
            View Live Site
          </button>
          <button className="flex items-center gap-2 rounded-full bg-[#116dff] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-[#0b5ce0] transition-colors">
            Edit Site
            <IoChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Business card */}
      <Card className="px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#3b5bff] to-[#8a2be2] text-white">
              <IoStorefrontOutline className="h-5 w-5" />
            </div>
            <span className="text-[16px] font-bold text-[#111]">My Store</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded bg-[#111] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
              Made with Stocko
            </span>
            <CollapseChevron open={storeOpen} onClick={() => setStoreOpen((v) => !v)} />
          </div>
        </div>

        {storeOpen && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[#f0f0f2] pt-3 text-[13px] text-[#555]">
            <span>
              Elite <Link to={storePath("account-settings/subscription-plans")} className="font-medium text-[#116dff]">Manage Plan</Link>
            </span>
            <span className="text-[#d5d6d9]">|</span>
            <span className="inline-flex items-center gap-1">
              <IoGlobeOutline className="h-4 w-4 text-[#888]" />
              <Link to={storePath("account-settings/website-domain")} className="font-medium text-[#116dff]">Connect Domain</Link>
            </span>
            <span className="text-[#d5d6d9]">|</span>
            <span>
              No business email <button className="font-medium text-[#116dff]">Connect</button>
            </span>
          </div>
        )}
      </Card>

      {/* Resources card */}
      <Card className="px-5 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-bold text-[#111]">Add a kit of resources to your Dashboard</h3>
          <CollapseChevron open={resourcesOpen} onClick={() => setResourcesOpen((v) => !v)} />
        </div>
        {resourcesOpen && (
          <div className="mt-3 flex items-center justify-between gap-6">
            <div>
              <p className="max-w-[560px] text-[14px] text-[#555]">
                Upload video tutorials, helpful files and other resources for your team to easily access here.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <button className="rounded-full bg-[#116dff] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-[#0b5ce0] transition-colors">
                  Get Started
                </button>
                <button className="rounded-full border border-[#dcdde0] px-4 py-2 text-[13.5px] font-semibold text-[#111] hover:bg-[#f4f4f6] transition-colors">
                  Learn More
                </button>
              </div>
            </div>
            <div className="hidden h-24 w-40 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#eaf0ff] to-[#f3e9ff] sm:flex">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#116dff] text-white">
                <IoEyeOutline className="h-5 w-5" />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Analytics card */}
      <Card className="px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-[16px] font-bold text-[#111]">Analytics</h3>
            <span className="rounded bg-[#f0f1f3] px-2 py-0.5 text-[11px] font-medium text-[#6b7280]">
              No live visitors
            </span>
          </div>
          <Link to={storePath("orders")} className="text-[13px] font-semibold text-[#116dff]">
            View All Reports
          </Link>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <p className="text-[13px] text-[#555]">
            Your key stats for the{" "}
            <button className="inline-flex items-center gap-1 font-medium text-[#116dff]">
              last 30 days <IoChevronDown className="h-3.5 w-3.5" />
            </button>
          </p>
          <button className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#116dff]">
            <IoAddOutline className="h-4 w-4" /> Add Stats
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-3">
          {/* No web analytics are collected, so there is no session count to show. */}
          <StatTile label="Site sessions" value="—" data={sparkline} />
          <StatTile
            label="Total sales"
            value={analytics ? formatCurrency(analytics.totalRevenue) : "—"}
            data={sparkline}
          />
          <StatTile
            label="Total orders"
            value={analytics ? String(analytics.totalOrders) : "—"}
            data={sparkline}
          />
          <StatTile label="Shifts" value={String(shifts.length)} data={sparkline} />
        </div>

        <p className="mt-3 text-[12px] text-[#8a8f99]">
          Updated now <button className="font-medium text-[#116dff]">Refresh</button>
        </p>

        {showAppPromo && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-[#f6f7f9] px-4 py-2.5">
            <p className="text-[13px] text-[#555]">
              Get analytics and reports on the go with the Stocko Owner app.
            </p>
            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-1.5 rounded-full border border-[#dcdde0] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#111] hover:bg-[#eef0f4] transition-colors">
                <IoPhonePortraitOutline className="h-4 w-4" /> Get the App
              </button>
              <button
                onClick={() => setShowAppPromo(false)}
                aria-label="Dismiss"
                className="text-[#9aa0aa] hover:text-[#111]"
              >
                <IoClose className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Activity feed */}
      <Card className="px-5 py-4">
        <h3 className="text-[16px] font-bold text-[#111]">Activity feed</h3>
        <p className="text-[13px] text-[#8a8f99]">Your most recent updates.</p>

        <div className="mt-4 flex flex-col divide-y divide-[#f0f0f2]">
          {[
            { title: "New order #TX-2025-0008 placed", time: "2 hours ago", tone: "bg-[#116dff]" },
            { title: "Stock low: Rose Velvet Body Lotion", time: "5 hours ago", tone: "bg-[#e0655b]" },
            { title: "Shift SH-2025-0006 verified", time: "Yesterday", tone: "bg-[#22a06b]" },
            { title: "Expense EXP-2025-0006 submitted", time: "2 days ago", tone: "bg-[#8a2be2]" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 py-3">
              <span className={`h-2 w-2 shrink-0 rounded-full ${item.tone}`} />
              <p className="flex-1 text-[13.5px] text-[#333]">{item.title}</p>
              <span className="text-[12px] text-[#9aa0aa]">{item.time}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default MainDashboard;
